import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Save, Building2, Globe, Eye, Calendar, Clock, Trash2, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { DATE_FORMATS, TIME_FORMATS, formatDate, formatTime } from '../utils/dateFormat';

import { API } from '../lib/apiBase';

export default function SettingsPage() {
  const { organization, refreshOrganization, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [orgData, setOrgData] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    address: ''
  });
  const [sharedFields, setSharedFields] = useState([]);
  const [cardFields, setCardFields] = useState([]);
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY');
  const [timeFormat, setTimeFormat] = useState('24h');
  const [cleanupModal, setCleanupModal] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  const availableSharedFields = [
    { key: 'phone_last5', label: 'Last 5 digits phone' },
    { key: 'first_name', label: 'First name' },
    { key: 'surname_initial', label: 'Surname initial' },
    { key: 'child_first_name', label: '1st child name' },
    { key: 'child_birth_month_year', label: 'Child birth month/year' },
    { key: 'diaper_size', label: 'Diaper size' },
  ];

  const availableCardFields = [
    { key: 'first_name', label: 'First Name' },
    { key: 'surname', label: 'Surname' },
    { key: 'phone_number', label: 'Phone' },
    { key: 'child_first_name', label: 'Child name' },
    { key: 'child_birthdate', label: 'Child birthdate' },
    { key: 'diaper_size', label: 'Diaper size' },
  ];

  useEffect(() => {
    if (organization) {
      setOrgData({
        name: organization.name || '',
        contact_email: organization.contact_email || '',
        contact_phone: organization.contact_phone || '',
        address: organization.address || ''
      });
      setSharedFields(organization.shared_fields || availableSharedFields.map(f => f.key));
      setCardFields(organization.card_fields || ['first_name', 'surname', 'phone_number', 'diaper_size']);
      setDateFormat(organization.date_format || 'DD/MM/YYYY');
      setTimeFormat(organization.time_format || '24h');
    }
  }, [organization]);

  const handleSaveOrganization = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/organizations/mine`, {
        ...orgData,
        shared_fields: sharedFields,
        card_fields: cardFields,
        date_format: dateFormat,
        time_format: timeFormat
      });
      await refreshOrganization();
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Error saving settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleSharedField = (key) => {
    if (sharedFields.includes(key)) {
      setSharedFields(sharedFields.filter(f => f !== key));
    } else {
      setSharedFields([...sharedFields, key]);
    }
  };

  const toggleCardField = (key) => {
    if (cardFields.includes(key)) {
      setCardFields(cardFields.filter(f => f !== key));
    } else {
      setCardFields([...cardFields, key]);
    }
  };

  const openCleanupModal = async () => {
    setCleanupModal(true);
    setCleanupPreview(null);
    try {
      const r = await axios.get(`${API}/admin/test-data-preview`);
      setCleanupPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load preview');
      setCleanupModal(false);
    }
  };

  const confirmCleanup = async () => {
    setCleanupLoading(true);
    try {
      const r = await axios.post(`${API}/admin/cleanup-test-data`);
      toast.success(
        `Removed ${r.data.deleted_organizations} test organizations ` +
        `(${r.data.deleted_users} users, ${r.data.deleted_beneficiaries} beneficiaries).`,
        { duration: 7000 }
      );
      setCleanupModal(false);
      setCleanupPreview(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cleanup failed');
    } finally {
      setCleanupLoading(false);
    }
  };

  // Preview date/time with current settings
  const previewDate = new Date().toISOString();

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="border-[#E5E5E5] max-w-md">
          <CardContent className="p-8 text-center">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-[#8C8C8C]" />
            <h2 className="text-xl font-semibold text-[#1A1A1A] mb-2">Limited Access</h2>
            <p className="text-[#595959]">
              Only administrators can modify settings.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] font-['Manrope']">Settings</h1>
        <p className="text-[#595959] mt-1">
          Configure your organization and display preferences
        </p>
      </div>

      {/* Organization Info */}
      <Card className="border-[#E5E5E5]">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#E9EFEA] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#4A5D4E]" />
            </div>
            <div>
              <CardTitle className="text-lg font-['Manrope']">Organization Information</CardTitle>
              <CardDescription>Contact details visible to other organizations</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Organization Name</Label>
            <Input
              value={orgData.name}
              onChange={(e) => setOrgData({ ...orgData, name: e.target.value })}
              data-testid="org-name-input"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={orgData.contact_email}
                onChange={(e) => setOrgData({ ...orgData, contact_email: e.target.value })}
                data-testid="org-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Contact Phone</Label>
              <Input
                value={orgData.contact_phone}
                onChange={(e) => setOrgData({ ...orgData, contact_phone: e.target.value })}
                data-testid="org-phone-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Address</Label>
            <Input
              value={orgData.address}
              onChange={(e) => setOrgData({ ...orgData, address: e.target.value })}
              placeholder="Street, ZIP, City"
              data-testid="org-address-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Date & Time Format */}
      <Card className="border-[#E5E5E5]">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#E9EFEA] flex items-center justify-center">
              <Calendar className="w-5 h-5 text-[#4A5D4E]" />
            </div>
            <div>
              <CardTitle className="text-lg font-['Manrope']">Date & Time Format</CardTitle>
              <CardDescription>Customize how dates and times are displayed</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Date Format
              </Label>
              <Select value={dateFormat} onValueChange={setDateFormat}>
                <SelectTrigger data-testid="date-format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_FORMATS.map(format => (
                    <SelectItem key={format.value} value={format.value}>
                      {format.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Time Format
              </Label>
              <Select value={timeFormat} onValueChange={setTimeFormat}>
                <SelectTrigger data-testid="time-format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_FORMATS.map(format => (
                    <SelectItem key={format.value} value={format.value}>
                      {format.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Preview */}
          <div className="bg-[#F0EFEA] rounded-lg p-4">
            <p className="text-sm font-medium text-[#595959] mb-2">Preview (current date/time):</p>
            <div className="flex flex-wrap gap-6">
              <div>
                <span className="text-xs text-[#8C8C8C]">Date:</span>
                <p className="text-lg font-semibold text-[#1A1A1A]">{formatDate(previewDate, dateFormat)}</p>
              </div>
              <div>
                <span className="text-xs text-[#8C8C8C]">Time:</span>
                <p className="text-lg font-semibold text-[#1A1A1A]">{formatTime(previewDate, timeFormat)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Shared Portal Fields */}
      <Card className="border-[#E5E5E5]">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#E9EFEA] flex items-center justify-center">
              <Globe className="w-5 h-5 text-[#4A5D4E]" />
            </div>
            <div>
              <CardTitle className="text-lg font-['Manrope']">Shared Portal</CardTitle>
              <CardDescription>Fields visible in the shared portal between organizations</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {availableSharedFields.map(field => (
              <div key={field.key} className="flex items-center gap-3 p-3 rounded-lg border border-[#E5E5E5] hover:bg-[#FAFAF8]">
                <Checkbox
                  checked={sharedFields.includes(field.key)}
                  onCheckedChange={() => toggleSharedField(field.key)}
                  data-testid={`shared-field-${field.key}`}
                />
                <span className="text-sm">{field.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Distribution Card Fields */}
      <Card className="border-[#E5E5E5]">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#FFF8E1] flex items-center justify-center">
              <Eye className="w-5 h-5 text-[#F57F17]" />
            </div>
            <div>
              <CardTitle className="text-lg font-['Manrope']">Distribution Cards</CardTitle>
              <CardDescription>Fields displayed on beneficiary distribution cards</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {availableCardFields.map(field => (
              <div key={field.key} className="flex items-center gap-3 p-3 rounded-lg border border-[#E5E5E5] hover:bg-[#FAFAF8]">
                <Checkbox
                  checked={cardFields.includes(field.key)}
                  onCheckedChange={() => toggleCardField(field.key)}
                  data-testid={`card-field-${field.key}`}
                />
                <span className="text-sm">{field.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          className="bg-[#4A5D4E] hover:bg-[#3D4D40]"
          onClick={handleSaveOrganization}
          disabled={loading}
          data-testid="save-settings-button"
        >
          <Save className="w-4 h-4 mr-2" />
          {loading ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Danger Zone */}
      <Card className="border-[#F5C6C6] bg-[#FDFBFB]">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#FDECEC] flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-[#B91C1C]" />
            </div>
            <div>
              <CardTitle className="font-['Manrope'] text-[#1A1A1A]">Danger Zone</CardTitle>
              <CardDescription>Irreversible maintenance operations</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 border border-[#F5C6C6] rounded-lg">
            <div className="flex-1">
              <p className="font-semibold text-[#1A1A1A] text-sm">Clean up test organizations</p>
              <p className="text-xs text-[#595959] mt-1">
                Permanently removes every organization whose users all have <span className="font-mono">@example.com</span> emails
                (test accounts created during development). Your own organization is never touched.
              </p>
            </div>
            <Button
              variant="outline"
              className="border-[#B91C1C] text-[#B91C1C] hover:bg-[#FDECEC] hover:text-[#B91C1C] w-full md:w-auto"
              onClick={openCleanupModal}
              data-testid="open-cleanup-modal-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Review &amp; clean up
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Cleanup Confirmation Modal */}
      <Dialog open={cleanupModal} onOpenChange={(open) => !cleanupLoading && setCleanupModal(open)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#B91C1C]" />
              Clean up test data
            </DialogTitle>
            <DialogDescription>
              Review the organizations that will be permanently deleted. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {!cleanupPreview ? (
            <div className="py-8 text-center text-[#8C8C8C]">Loading preview...</div>
          ) : cleanupPreview.total_organizations === 0 ? (
            <div className="py-8 text-center text-[#8C8C8C]">
              <p className="font-medium">No test organizations found.</p>
              <p className="text-xs mt-1">Nothing to clean up — your database is already clean.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto" data-testid="cleanup-preview-list">
              <p className="text-sm text-[#595959]">
                Found <strong>{cleanupPreview.total_organizations}</strong> test organizations:
              </p>
              {cleanupPreview.organizations.map((org) => (
                <div key={org.id} className="border border-[#E5E5E5] rounded-lg p-3 bg-white">
                  <p className="font-semibold text-sm text-[#1A1A1A]">{org.name}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-[#595959]">
                    <span>{org.user_count} user{org.user_count !== 1 ? 's' : ''}</span>
                    <span>{org.beneficiary_count} beneficiar{org.beneficiary_count !== 1 ? 'ies' : 'y'}</span>
                    <span>{org.waiting_count} waiting list entr{org.waiting_count !== 1 ? 'ies' : 'y'}</span>
                  </div>
                  <p className="text-[10px] text-[#8C8C8C] mt-1 truncate">
                    {org.user_emails.join(', ')}
                  </p>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCleanupModal(false)}
              disabled={cleanupLoading}
              data-testid="cancel-cleanup-button"
            >
              Cancel
            </Button>
            {cleanupPreview?.total_organizations > 0 && (
              <Button
                className="bg-[#B91C1C] hover:bg-[#991717]"
                onClick={confirmCleanup}
                disabled={cleanupLoading}
                data-testid="confirm-cleanup-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {cleanupLoading
                  ? 'Deleting...'
                  : `Delete ${cleanupPreview.total_organizations} organization${cleanupPreview.total_organizations !== 1 ? 's' : ''}`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
